import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const PdfDownloadButton = ({ targetId = 'pdf-content', filename = 'monitoring-report' }) => {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    const element = document.getElementById(targetId);
    if (!element) return;

    setDownloading(true);
    await new Promise(r => setTimeout(r, 50));

    try {
      const canvas = await html2canvas(element, {
        scale: 1,
        useCORS: true,
        logging: false,
        backgroundColor: '#FFFFFF',
        windowWidth: 1400,
        scrollY: -window.scrollY
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.8);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position -= pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`${filename}.pdf`);
    } catch (error) {
      console.error('PDF error:', error);
      window.print();
    } finally {
      setDownloading(false);
    }
  }, [targetId, filename]);

  return (
    <button
      onClick={handleDownload}
      disabled={downloading}
      className="rounded-none font-bold tracking-widest uppercase text-xs px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white transition-transform active:translate-y-1 flex items-center gap-2 disabled:opacity-50"
      data-testid="pdf-download-button"
    >
      <Download className={`w-4 h-4 ${downloading ? 'animate-bounce' : ''}`} />
      {downloading ? 'Generating PDF...' : 'Download PDF'}
    </button>
  );
};

export default PdfDownloadButton;
